import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AnalyticsDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const getApiBaseUrl = () => {
    if (process.env.NODE_ENV === 'production') {
      return window.location.origin;
    } else {
      return 'https://tailorapi.mentoran.in';
    }
  };

  const fetchAnalytics = async () => {
    try {
      setError('');
      const baseUrl = getApiBaseUrl();
      const response = await axios.get(`${baseUrl}/api/analytics/stats`);
      console.log('📊 Analytics data received:', response.data);
      setStats(response.data);
    } catch (error) {
      console.error('❌ Error fetching analytics:', error);
      setError('Failed to load analytics data. Make sure you have visited some public pages first.');
    } finally {
      setLoading(false);
    }
  };

  const resetAnalytics = async () => {
    if (window.confirm('Are you sure you want to reset all analytics data? This cannot be undone.')) {
      try {
        const baseUrl = getApiBaseUrl();
        await axios.delete(`${baseUrl}/api/analytics/reset`);
        fetchAnalytics(); // Refresh stats
        alert('Analytics data reset successfully!');
      } catch (error) {
        console.error('Error resetting analytics:', error);
        alert('Failed to reset analytics data.');
      }
    }
  };

  const debugAnalytics = async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const response = await axios.get(`${baseUrl}/api/analytics/debug`);
      console.log('🐛 Debug analytics data:', response.data);
      alert('Check browser console for debug data');
    } catch (error) {
      console.error('Error fetching debug data:', error);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    // Refresh every 30 seconds
    const interval = setInterval(fetchAnalytics, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center p-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading analytics data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-warning text-center">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
          <div className="mt-3">
            <button className="btn btn-primary btn-sm me-2" onClick={fetchAnalytics}>
              <i className="fas fa-redo me-1"></i>
              Try Again
            </button>
            <button className="btn btn-outline-secondary btn-sm" onClick={debugAnalytics}>
              <i className="fas fa-bug me-1"></i>
              Debug
            </button>
          </div>
          <div className="mt-2 small text-muted">
            Tip: Visit your public website pages first to generate analytics data.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-0">Website Analytics</h2>
          <p className="text-muted mb-0">Real-time tracking of website visitor activity</p>
        </div>
        <div>
          <button className="btn btn-outline-secondary btn-sm me-2" onClick={debugAnalytics}>
            <i className="fas fa-bug me-1"></i>
            Debug
          </button>
          <button className="btn btn-outline-danger btn-sm me-2" onClick={resetAnalytics}>
            <i className="fas fa-trash me-1"></i>
            Reset Data
          </button>
          <button className="btn btn-outline-primary btn-sm" onClick={fetchAnalytics}>
            <i className="fas fa-sync-alt me-1"></i>
            Refresh
          </button>
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="row mb-4">
        <div className="col-md-2 mb-3">
          <div className="card bg-primary text-white">
            <div className="card-body text-center">
              <i className="fas fa-eye fs-2 mb-2"></i>
              <h5 className="card-title">Total Views</h5>
              <h2 className="display-6 mb-0">{stats?.summary?.totalPageViews || 0}</h2>
            </div>
          </div>
        </div>
        
        <div className="col-md-2 mb-3">
          <div className="card bg-success text-white">
            <div className="card-body text-center">
              <i className="fas fa-users fs-2 mb-2"></i>
              <h5 className="card-title">Total Sessions</h5>
              <h2 className="display-6 mb-0">{stats?.summary?.totalSessions || 0}</h2>
            </div>
          </div>
        </div>
        
        <div className="col-md-2 mb-3">
          <div className="card bg-info text-white">
            <div className="card-body text-center">
              <i className="fas fa-chart-line fs-2 mb-2"></i>
              <h5 className="card-title">24h Views</h5>
              <h2 className="display-6 mb-0">{stats?.summary?.pageViews24h || 0}</h2>
            </div>
          </div>
        </div>
        
        <div className="col-md-2 mb-3">
          <div className="card bg-warning text-white">
            <div className="card-body text-center">
              <i className="fas fa-user-clock fs-2 mb-2"></i>
              <h5 className="card-title">24h Sessions</h5>
              <h2 className="display-6 mb-0">{stats?.summary?.sessions24h || 0}</h2>
            </div>
          </div>
        </div>
        
        <div className="col-md-2 mb-3">
          <div className="card bg-secondary text-white">
            <div className="card-body text-center">
              <i className="fas fa-bell fs-2 mb-2"></i>
              <h5 className="card-title">Total Events</h5>
              <h2 className="display-6 mb-0">{stats?.summary?.totalEvents || 0}</h2>
            </div>
          </div>
        </div>
        
        <div className="col-md-2 mb-3">
          <div className="card bg-dark text-white">
            <div className="card-body text-center">
              <i className="fas fa-bolt fs-2 mb-2"></i>
              <h5 className="card-title">24h Events</h5>
              <h2 className="display-6 mb-0">{stats?.summary?.events24h || 0}</h2>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        {/* Popular Pages */}
        <div className="col-md-6 mb-4">
          <div className="card h-100">
            <div className="card-header bg-light d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">
                <i className="fas fa-star me-2 text-warning"></i>
                Popular Pages (24h)
              </h5>
              <span className="badge bg-primary">{stats?.popularPages?.length || 0}</span>
            </div>
            <div className="card-body">
              {stats?.popularPages && stats.popularPages.length > 0 ? (
                <div className="list-group list-group-flush">
                  {stats.popularPages.map((page, index) => (
                    <div key={index} className="list-group-item d-flex justify-content-between align-items-center px-0">
                      <span className="text-truncate" title={page.path}>
                        {page.path === '/' ? 'Homepage' : page.path}
                      </span>
                      <span className="badge bg-primary rounded-pill">{page.views}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted py-4">
                  <i className="fas fa-inbox fa-2x mb-2"></i>
                  <p>No page views in the last 24 hours</p>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Recent Activity */}
        <div className="col-md-6 mb-4">
          <div className="card h-100">
            <div className="card-header bg-light d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">
                <i className="fas fa-clock me-2 text-info"></i>
                Recent Activity (24h)
              </h5>
              <span className="badge bg-info">{stats?.recentActivity?.length || 0}</span>
            </div>
            <div className="card-body">
              {stats?.recentActivity && stats.recentActivity.length > 0 ? (
                <div className="list-group list-group-flush">
                  {stats.recentActivity.map((activity, index) => (
                    <div key={index} className="list-group-item px-0">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <div className="fw-medium text-truncate">
                            {activity.path === '/' ? 'Homepage' : activity.path}
                          </div>
                          <small className="text-muted">
                            {activity.time} • {activity.screen}
                          </small>
                        </div>
                        <small className="text-muted text-nowrap ms-2">
                          {activity.referrer === 'direct' ? 'Direct' : 
                           activity.referrer.length > 20 ? activity.referrer.substring(0, 20) + '...' : activity.referrer}
                        </small>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted py-4">
                  <i className="fas fa-history fa-2x mb-2"></i>
                  <p>No recent activity</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Traffic Sources */}
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header bg-light d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">
                <i className="fas fa-traffic-light me-2 text-success"></i>
                Traffic Sources (24h)
              </h5>
              <span className="badge bg-success">{stats?.trafficSources?.length || 0}</span>
            </div>
            <div className="card-body">
              {stats?.trafficSources && stats.trafficSources.length > 0 ? (
                <div className="row">
                  {stats.trafficSources.map((source, index) => (
                    <div key={index} className="col-md-4 mb-2">
                      <div className="d-flex justify-content-between align-items-center p-3 border rounded bg-light">
                        <span className="fw-medium text-truncate" title={source.source}>
                          {source.source === 'direct' ? 'Direct Traffic' : source.source}
                        </span>
                        <span className="badge bg-success fs-6">{source.visits}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted py-4">
                  <i className="fas fa-compass fa-2x mb-2"></i>
                  <p>No traffic source data available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;